/**
 * Pass 3c — the rounds that hit nobody, swept against what is standing on the
 * ground (AD-018).
 *
 * ## Why it is a pass of its own and not a line in `gun-pass.ts`
 *
 * `searchlight-posts.ts` got its whole feature for free by riding
 * `gun-pass.ts`'s existing collision list, and that was the right answer for
 * *twelve lamps*. It is the wrong answer for two hundred cottages: the gunnery
 * list is built per shooter, per tick, as an array of spheres, and putting the
 * region's scenery into it would cost every shooter two hundred sphere tests a
 * tick to answer a question that is nearly always "no" — and would put every
 * building in front of `resolveGunnery`, which would start *aiming* at them.
 *
 * So the scenery test runs **after** the gunnery sweep, over its survivors
 * only. That ordering is not an optimisation, it is the correctness rule: a
 * round that would pass through an aeroplane and into the barn behind it hits
 * the aeroplane, because `collide.ts` already consumed it. Nothing can be shot
 * *through* a building either, because a round that goes into one is consumed
 * here in turn.
 *
 * The sweep is the same segment `collide.ts` used — `roundTravelM` at the
 * previous tick and at this one — so a round is tested over exactly the path it
 * flew and nothing is tested twice. The broad phase, the box test and the
 * spatial index all live behind `SceneryQuery` on the caller's side, because
 * the geometry they index is the web app's `.WLD` region and the engine has no
 * business holding it (see `scenery-fires.ts`'s module note).
 */

import type { Tunables } from "../flight/tunables.ts";
import type { Vec3 } from "../flight/math3d.ts";
import type { CombatAircraft } from "./encounter.ts";
import { roundTravelM, type RoundRecord } from "./rounds.ts";
import {
  advanceSceneryFires,
  strikeScenery,
  type SceneryFire,
  type SceneryQuery,
} from "./scenery-fires.ts";
import type { AmmunitionType } from "./target-kinds.ts";

export interface SceneryPassInput {
  /** The gunnery pass's survivors — rounds that ran into nothing that flies. */
  readonly rounds: readonly RoundRecord[];
  readonly fires: readonly SceneryFire[];
  readonly aircraft: readonly CombatAircraft[];
  readonly scenery: SceneryQuery;
  readonly tick: number;
  readonly dt: number;
  readonly tunables: Tunables;
}

export interface SceneryPass {
  /** Rounds still in the air — every one that did not go into a wall. */
  readonly rounds: readonly RoundRecord[];
  readonly fires: readonly SceneryFire[];
}

function pointAlong(round: RoundRecord, travelM: number, into: MutablePoint): Vec3 {
  into.x = round.from.x + round.direction.x * travelM;
  into.y = round.from.y + round.direction.y * travelM;
  into.z = round.from.z + round.direction.z * travelM;
  return into;
}

interface MutablePoint {
  x: number;
  y: number;
  z: number;
}

/** What was in the belt. Same lookup `encounter-tick.ts` uses for the gasbag damage model. */
function ammunitionOf(roster: readonly CombatAircraft[], shooterId: string): AmmunitionType {
  return roster.find((entry) => entry.id === shooterId)?.ammunition ?? "regular";
}

/**
 * The pass. Ages the fires first and then adds this tick's, so a structure lit
 * on this very tick gets a full `burnSeconds` rather than one tick less.
 *
 * The two scratch points are module-free locals rather than shared state: this
 * runs once a tick over a list that is empty on most of them, and a hidden
 * mutable singleton in a deterministic core is worth more than the allocation
 * it saves.
 */
export function sceneryPass(input: SceneryPassInput): SceneryPass {
  let fires = advanceSceneryFires(input.fires, input.dt);
  if (input.rounds.length === 0) return { rounds: input.rounds, fires };

  const from: MutablePoint = { x: 0, y: 0, z: 0 };
  const to: MutablePoint = { x: 0, y: 0, z: 0 };
  const survivors: RoundRecord[] = [];
  let struck = false;
  for (const round of input.rounds) {
    const startM = roundTravelM(round, input.tick - 1, input.dt, input.tunables);
    const endM = roundTravelM(round, input.tick, input.dt, input.tunables);
    if (endM <= startM) {
      survivors.push(round);
      continue;
    }
    const structure = input.scenery.structureOnSegment(
      pointAlong(round, startM, from),
      pointAlong(round, endM, to),
    );
    if (structure === null) {
      survivors.push(round);
      continue;
    }
    struck = true;
    fires = strikeScenery(fires, structure, ammunitionOf(input.aircraft, round.shooterId));
  }
  // The survivor list is only swapped in when something actually stopped, so a
  // tick where nothing hit a wall — which is nearly all of them — hands back the
  // identical array the gunnery pass produced.
  return { rounds: struck ? survivors : input.rounds, fires };
}
